import {BrandFeaturesType} from '../../../../provider/brand-features/brand-features-context-type';
import {LangKeyType} from '../../../../provider/locale/translation/type';
import {FeaturesEnum} from '../../../../service/user/user-type';

import {isOnlyEmojis} from './review-list/review/review-helper';
import {ReviewDataWithCatalogType} from './review-list/review-list-type';

/**
 * Why a selected review is left out of a bulk generation run. Mirrors the conditions that hide or
 * disable the single-review "Generate reply" button, so what the bulk action does matches what the
 * user already sees on each card.
 */
export enum BulkAiGenerationSkipReasonEnum {
    CatalogNotSupported = 'catalogNotSupported',
    AlreadyAnswered = 'alreadyAnswered',
    NoText = 'noText',
    NoBrandLicence = 'noBrandLicence',
    TestBrand = 'testBrand',
    HasDraft = 'hasDraft',
}

export const bulkAiGenerationSkipReasonLangKeyMap: Record<BulkAiGenerationSkipReasonEnum, LangKeyType> = {
    [BulkAiGenerationSkipReasonEnum.CatalogNotSupported]: 'REVIEWS__BULK_AI__SKIP_REASON__CATALOG_NOT_SUPPORTED',
    [BulkAiGenerationSkipReasonEnum.AlreadyAnswered]: 'REVIEWS__BULK_AI__SKIP_REASON__ALREADY_ANSWERED',
    [BulkAiGenerationSkipReasonEnum.NoText]: 'REVIEWS__BULK_AI__SKIP_REASON__NO_TEXT',
    [BulkAiGenerationSkipReasonEnum.NoBrandLicence]: 'REVIEWS__BULK_AI__SKIP_REASON__NO_BRAND_LICENCE',
    [BulkAiGenerationSkipReasonEnum.TestBrand]: 'REVIEWS__BULK_AI__SKIP_REASON__TEST_BRAND',
    [BulkAiGenerationSkipReasonEnum.HasDraft]: 'REVIEWS__BULK_AI__SKIP_REASON__HAS_DRAFT',
};

export type BulkAiRunContextType = {
    featuresByBrand: Record<string, BrandFeaturesType>;
    isSending: boolean;
    hasDraft: (reviewId: number) => boolean;
};

/**
 * A brand's features are its own active licences, and the backend checks the generation licence
 * against the review's brand — so the account-wide flag alone would let through reviews the
 * endpoint is going to refuse with a 400.
 */
function hasBrandGenerationLicence(
    review: ReviewDataWithCatalogType,
    featuresByBrand: Record<string, BrandFeaturesType>
): boolean {
    const brandFeatures = featuresByBrand[String(review.brand.id)];

    // No entry at all means the brand list has not loaded yet: stay permissive rather than
    // greying the action out on a slow request — the backend still guards the call.
    if (!brandFeatures) {
        return true;
    }

    return FeaturesEnum.reviewGptReplySuggestions in brandFeatures;
}

function getBulkAiGenerationSkipReason(
    review: ReviewDataWithCatalogType,
    featuresByBrand: Record<string, BrandFeaturesType>
): BulkAiGenerationSkipReasonEnum | null {
    const {ableToReply, catalog, children, comment, deletedAt, isHidden} = review;

    if (children.length > 0) {
        return BulkAiGenerationSkipReasonEnum.AlreadyAnswered;
    }

    if (deletedAt || isHidden || !ableToReply || !catalog?.gpt_suggest_supported) {
        return BulkAiGenerationSkipReasonEnum.CatalogNotSupported;
    }

    if (!comment || isOnlyEmojis(comment)) {
        return BulkAiGenerationSkipReasonEnum.NoText;
    }

    if (!hasBrandGenerationLicence(review, featuresByBrand)) {
        return BulkAiGenerationSkipReasonEnum.NoBrandLicence;
    }

    return null;
}

export function isBulkAiGenerationAllowed(
    review: ReviewDataWithCatalogType,
    featuresByBrand: Record<string, BrandFeaturesType>
): boolean {
    return getBulkAiGenerationSkipReason(review, featuresByBrand) === null;
}

/**
 * Publishing is blocked on test brands the same way the single-review send button is.
 */
export function isBulkAiSendAllowed(
    review: ReviewDataWithCatalogType,
    featuresByBrand: Record<string, BrandFeaturesType>
): boolean {
    return isBulkAiGenerationAllowed(review, featuresByBrand) && !review.brand.isTest;
}

/**
 * On top of the per-card rules, a run leaves out test brands when it is going to publish — they
 * would only fail at send time — and reviews the user has already started typing a reply to, since
 * generation overwrites the form.
 */
export function getBulkAiRunSkipReason(
    review: ReviewDataWithCatalogType,
    context: BulkAiRunContextType
): BulkAiGenerationSkipReasonEnum | null {
    const generationSkipReason = getBulkAiGenerationSkipReason(review, context.featuresByBrand);

    if (generationSkipReason) {
        return generationSkipReason;
    }

    if (context.isSending && review.brand.isTest) {
        return BulkAiGenerationSkipReasonEnum.TestBrand;
    }

    if (context.hasDraft(review.id)) {
        return BulkAiGenerationSkipReasonEnum.HasDraft;
    }

    return null;
}

export function countBulkAiSkipReasons(
    reviews: Array<ReviewDataWithCatalogType>,
    context: BulkAiRunContextType
): Array<{reason: BulkAiGenerationSkipReasonEnum; count: number}> {
    const counters = reviews.reduce<Partial<Record<BulkAiGenerationSkipReasonEnum, number>>>((accumulator, review) => {
        const reason = getBulkAiRunSkipReason(review, context);

        if (!reason) {
            return accumulator;
        }

        return {...accumulator, [reason]: (accumulator[reason] ?? 0) + 1};
    }, {});

    return Object.values(BulkAiGenerationSkipReasonEnum)
        .map((reason) => ({reason, count: counters[reason] ?? 0}))
        .filter((item) => item.count > 0);
}
